import React from "react";
import { Box, Typography } from "@mui/material";
import {
    androidLightTokens,
    androidPlayGearColor,
    androidPlayToolbarBg,
    androidPlayToolbarBorder,
    androidToolbarColors,
} from "../styles/androidJigsawTokens";

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';

function ToolSvg({ children, size = 22 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
            {children}
        </svg>
    );
}

/** Icon paths copied from android-jigsaw-muse `PlayToolbar.js`. */
const TOOL_ICONS = {
    mute: (c) => (
        <>
            <path d="M4 9.2h3.2L11.5 5v14l-4.3-4.2H4V9.2z" fill={c} />
            <path d="M14.2 8.6a4.2 4.2 0 010 6.8" stroke={c} strokeWidth={2.2} strokeLinecap="round" fill="none" />
            <path d="M16.6 6.4a7.2 7.2 0 010 11.2" stroke={c} strokeWidth={2.2} strokeLinecap="round" fill="none" />
        </>
    ),
    sweep: (c) => (
        <>
            <path d="M9 2.5h3.2l1.1 7.2H8.2L9 2.5z" fill={c} />
            <path d="M4 11h16l-1.6 9.2a1.6 1.6 0 01-1.6 1.3H7.2a1.6 1.6 0 01-1.6-1.3L4 11z" fill={c} />
        </>
    ),
    hint: (c) => (
        <>
            <path d="M12 2.2a6.2 6.2 0 00-3.6 11.2c.5.4.9 1 .9 1.6V16.4h5.4v-1.4c0-.6.4-1.2.9-1.6A6.2 6.2 0 0012 2.2z" fill={c} />
            <path d="M9.4 18h5.2v1.1a2.6 2.6 0 01-5.2 0V18z" fill={c} />
        </>
    ),
    zoomIn: (c) => (
        <>
            <path d="M10.2 3.2a7 7 0 104.4 12.4l4.3 4.3 1.8-1.8-4.3-4.3A7 7 0 0010.2 3.2z" fill={c} />
            <path d="M10.2 6.4v7.6M6.4 10.2h7.6" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
        </>
    ),
    zoomOut: (c) => (
        <>
            <path d="M10.2 3.2a7 7 0 104.4 12.4l4.3 4.3 1.8-1.8-4.3-4.3A7 7 0 0010.2 3.2z" fill={c} />
            <path d="M6.4 10.2h7.6" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
        </>
    ),
    preview: (c) => (
        <>
            <path d="M3.5 5.5h17a1.5 1.5 0 011.5 1.5v11a1.5 1.5 0 01-1.5 1.5h-17A1.5 1.5 0 012 18V7a1.5 1.5 0 011.5-1.5z" fill={c} />
            <path d="M5 16.2l3.6-3.6 2.4 2.4 2.8-3.6 4.2 4.8H5z" fill="#fff" />
            <path d="M8.2 9.6a1.3 1.3 0 100-2.6 1.3 1.3 0 000 2.6z" fill="#fff" />
        </>
    ),
    basket: (c) => (
        <>
            <path d="M6.2 8.2L8.6 3.4h1.6L8.4 8.2H6.2zm9.6 0L14 3.4h1.6l2.2 4.8h-1.8z" fill={c} />
            <path d="M3.6 9.2h16.8l-1.5 9.4a2 2 0 01-2 1.7H7.1a2 2 0 01-2-1.7L3.6 9.2z" fill={c} />
        </>
    ),
};

const TOOLBAR_ORDER = ["mute", "sweep", "hint", "zoomIn", "zoomOut", "preview", "basket"];

function GearIcon({ color }) {
    return (
        <svg width={22} height={22} viewBox="0 0 24 24" fill={color} aria-hidden focusable="false">
            <path
                fillRule="evenodd"
                d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87a.49.49 0 00-.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 00-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1112 8.4a3.6 3.6 0 010 7.2z"
            />
        </svg>
    );
}

/**
 * Android `PlayScreen` header row: ‹ chevron | centred puzzle title | orange gear + timer.
 * The puzzle title lives here (no separate title / difficulty bar under the board in the app).
 */
export function ApplePlayNavBar({ title, clock = "0:42", backgroundColor = androidLightTokens.shellBg }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 4,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                minHeight: 48,
                px: 1,
                bgcolor: backgroundColor,
            }}
        >
            <Box
                sx={{
                    minWidth: 44,
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: androidLightTokens.shellText,
                    fontFamily: FONT,
                    fontSize: 34,
                    fontWeight: 300,
                    lineHeight: "40px",
                    pb: "4px",
                }}
                aria-hidden
            >
                ‹
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, px: 0.75, display: "flex", justifyContent: "center" }}>
                <Typography
                    component="h1"
                    noWrap
                    sx={{
                        m: 0,
                        fontFamily: FONT,
                        fontSize: 15,
                        fontWeight: 700,
                        textAlign: "center",
                        color: androidLightTokens.shellText,
                    }}
                >
                    {title || "Jigsaw"}
                </Typography>
            </Box>
            <Box sx={{ minWidth: 44, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ minWidth: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <GearIcon color={androidPlayGearColor} />
                </Box>
                <Typography
                    component="span"
                    sx={{
                        mt: "-2px",
                        fontFamily: FONT,
                        fontSize: 11,
                        fontVariantNumeric: "tabular-nums",
                        color: androidLightTokens.shellTextMuted,
                        textAlign: "center",
                    }}
                >
                    {clock}
                </Typography>
            </Box>
        </Box>
    );
}

/**
 * Android `PlayToolbar` strip: mute, sweep, hint (+ coin badge), zoom in/out, original, color baskets.
 * White bar with a hairline bottom border (light theme).
 */
export function ApplePlayToolbar({ hintCount = 12 }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                flexShrink: 0,
                bgcolor: androidPlayToolbarBg,
                borderBottom: `1px solid ${androidPlayToolbarBorder}`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    minHeight: 44,
                    px: 0.5,
                }}
            >
                {TOOLBAR_ORDER.map((key) => {
                    const color = androidToolbarColors[key];
                    return (
                        <Box
                            key={key}
                            sx={{
                                position: "relative",
                                minWidth: 44,
                                minHeight: 44,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ToolSvg>{TOOL_ICONS[key](color)}</ToolSvg>
                            {key === "hint" ? (
                                <Box
                                    component="span"
                                    sx={{
                                        position: "absolute",
                                        top: "2px",
                                        right: "2px",
                                        minWidth: 14,
                                        px: "3px",
                                        borderRadius: "8px",
                                        border: `1px solid ${color}`,
                                        color,
                                        fontFamily: FONT,
                                        fontSize: 9,
                                        fontWeight: 800,
                                        textAlign: "center",
                                        lineHeight: "12px",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {hintCount}
                                </Box>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
